import * as React from "react";
import { createPortal } from "react-dom";

import { cn } from "@/shared/lib/utils";
import type { RestrictedWord } from "@/shared/cache/slices/restrictedWords";
import {
  type RestrictedWordMatch,
  detectRestrictedWords,
  mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import { NarrativeAbbreviationsDialog } from "./NarrativeAbbreviationsDialog";

// ── Public types ───────────────────────────────────────────────────

/** A text abbreviation that auto-expands as the user types. */
export interface Abbreviation {
  /** The trigger text the user types (case-sensitive) */
  trigger: string;
  /** The text it expands into */
  replacement: string;
}

export interface NarrativeTextAreaProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  id?: string;
  /** Restricted words to detect and highlight */
  restrictedWords?: RestrictedWord[];
  /** Abbreviations that auto-expand on space */
  abbreviations?: Abbreviation[];
  /** Error message to display below */
  error?: string;
  disabled?: boolean;
}

export interface NarrativeTextAreaRef {
  /** Returns current restricted word matches */
  getMatches: () => RestrictedWordMatch[];
  /** Whether the textarea currently contains any restricted words */
  hasRestrictedWords: () => boolean;
  /** Whether the textarea contains block-severity restricted words */
  hasBlockingWords: () => boolean;
  /** Whether the textarea contains warn-severity restricted words */
  hasWarningWords: () => boolean;
}

// ── Abbreviation lookup builder ────────────────────────────────────

function buildAbbreviationMap(
  abbreviations: Abbreviation[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const abbr of abbreviations) {
    map.set(abbr.trigger, abbr.replacement);
  }
  return map;
}

// ── Component ──────────────────────────────────────────────────────

export const NarrativeTextArea = React.forwardRef<
  NarrativeTextAreaRef,
  NarrativeTextAreaProps
>(
  (
    {
      value,
      onChange,
      onBlur,
      placeholder = "Type your task description here.",
      className,
      id,
      restrictedWords,
      abbreviations,
      error,
      disabled = false,
    },
    ref,
  ) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const mirrorRef = React.useRef<HTMLDivElement>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const hasRestrictedWordsConfig = !!restrictedWords?.length;
    const hasAbbreviations = !!abbreviations?.length;

    // ── Abbreviation map ─────────────────────────────────────────────

    const abbrMap = React.useMemo(
      () => (hasAbbreviations ? buildAbbreviationMap(abbreviations!) : null),
      [abbreviations, hasAbbreviations],
    );

    // ── Restricted word detection ────────────────────────────────────

    const matches = React.useMemo(
      () =>
        hasRestrictedWordsConfig
          ? mergeOverlappingMatches(
              detectRestrictedWords(value, restrictedWords!),
            )
          : [],
      [value, restrictedWords, hasRestrictedWordsConfig],
    );

    const hasMatchesFlag = matches.length > 0;
    const hasBlocking = matches.some((m) => m.severity === "block");
    const hasWarnings = matches.some((m) => m.severity === "warning");

    React.useImperativeHandle(
      ref,
      () => ({
        getMatches: () => matches,
        hasRestrictedWords: () => hasMatchesFlag,
        hasBlockingWords: () => hasBlocking,
        hasWarningWords: () => hasWarnings,
      }),
      [matches, hasMatchesFlag, hasBlocking, hasWarnings],
    );

    // ── Change handler with abbreviation expansion ──────────────────

    const handleChange = React.useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textarea = textareaRef.current;
        if (!textarea) {
          onChange(e.target.value);
          return;
        }

        let text = e.target.value;

        // Only expand when a single space was just inserted (not on
        // deletion, paste, or other edits). Comparing lengths works
        // across all platforms including virtual keyboards on mobile.
        const wasSpaceInserted =
          abbrMap &&
          text.length === value.length + 1 &&
          text[textarea.selectionStart - 1] === " ";

        if (wasSpaceInserted) {
          const cursorPos = textarea.selectionStart;
          const beforeCursor = text.slice(0, cursorPos);
          const afterCursor = text.slice(cursorPos);
          const match = beforeCursor.match(/(\b\w+)\s$/);

          if (match) {
            const replacement = abbrMap.get(match[1]);
            if (replacement) {
              const wordStart = beforeCursor.lastIndexOf(match[1]);
              const newBefore =
                beforeCursor.slice(0, wordStart) + replacement + " ";
              text = newBefore + afterCursor;

              onChange(text);

              requestAnimationFrame(() => {
                textarea.setSelectionRange(newBefore.length, newBefore.length);
              });
              return;
            }
          }
        }

        onChange(text);
      },
      [onChange, abbrMap, value],
    );

    // ── Visual overlay (behind textarea, no interaction) ─────────

    const overlay = React.useMemo(() => {
      if (!hasMatchesFlag) return null;

      const segments: {
        text: string;
        match: RestrictedWordMatch | null;
      }[] = [];
      let cursor = 0;

      for (const m of matches) {
        if (m.start > cursor) {
          segments.push({
            text: value.slice(cursor, m.start),
            match: null,
          });
        }
        segments.push({
          text: value.slice(m.start, m.end),
          match: m,
        });
        cursor = m.end;
      }
      if (cursor < value.length) {
        segments.push({ text: value.slice(cursor), match: null });
      }

      return (
        <div
          ref={overlayRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-sm text-transparent"
          style={{ overflowY: "auto", scrollbarGutter: "stable", willChange: "scroll-position" }}
        >
          {segments.map((seg, i) =>
            seg.match ? (
              <mark
                key={i}
                className={cn(
                  "text-transparent rounded-sm",
                  seg.match.severity === "block"
                    ? "bg-red-200/70 dark:bg-red-500/25"
                    : "bg-amber-200/70 dark:bg-amber-500/25",
                )}
              >
                {seg.text}
              </mark>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </div>
      );
    }, [value, matches, hasMatchesFlag]);

    // ── Hover tooltip on restricted words (plain fixed div) ──────

    const [abbreviationsOpen, setAbbreviationsOpen] = React.useState(false);

    const [tooltip, setTooltip] = React.useState<{
      severity: "warning" | "block";
      message: string;
      suggestion: string | null;
      text: string;
      x: number;
      y: number;
      startIndex: number;
    } | null>(null);

    // Debounce refs for tooltip transitions
    const tooltipTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const currentWordRef = React.useRef<number | null>(null);

    // Cleanup tooltip timer on unmount
    React.useEffect(() => {
      return () => {
        if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      };
    }, []);

    // Sync mirror + overlay scroll with textarea
    const handleScroll = React.useCallback(() => {
      if (textareaRef.current) {
        const scrollTop = textareaRef.current.scrollTop;
        if (mirrorRef.current) mirrorRef.current.scrollTop = scrollTop;
        if (overlayRef.current) overlayRef.current.scrollTop = scrollTop;
      }
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      currentWordRef.current = null;
      setTooltip(null);
    }, []);

    // ResizeObserver for belt-and-suspenders scroll sync
    React.useEffect(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const observer = new ResizeObserver(() => {
        const scrollTop = textarea.scrollTop;
        if (mirrorRef.current) mirrorRef.current.scrollTop = scrollTop;
        if (overlayRef.current) overlayRef.current.scrollTop = scrollTop;
      });
      observer.observe(textarea);
      return () => observer.disconnect();
    }, []);

    // Hit-test helper: find restricted word span under mouse
    const hitTestRestricted = React.useCallback(
      (clientX: number, clientY: number) => {
        if (!mirrorRef.current || !containerRef.current) return null;

        const spans = mirrorRef.current.querySelectorAll<HTMLElement>(
          "[data-restricted]",
        );
        const containerRect = containerRef.current.getBoundingClientRect();

        for (const span of spans) {
          const sr = span.getBoundingClientRect();
          if (
            clientX >= sr.left &&
            clientX <= sr.right &&
            clientY >= sr.top &&
            clientY <= sr.bottom
          ) {
            return {
              severity: (span.dataset.severity || "block") as
                | "warning"
                | "block",
              message: span.dataset.message || "",
              suggestion: span.dataset.suggestion || null,
              text: span.dataset.text || "",
              x: sr.left - containerRect.left + sr.width / 2,
              y: sr.top - containerRect.top,
              startIndex: parseInt(span.dataset.start || "0", 10),
            };
          }
        }
        return null;
      },
      [],
    );

    const handleMouseMove = React.useCallback(
      (e: React.MouseEvent) => {
        if (!hasMatchesFlag) {
          if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
          currentWordRef.current = null;
          setTooltip(null);
          return;
        }

        const found = hitTestRestricted(e.clientX, e.clientY);

        if (found) {
          // Same word as current — skip (no re-render)
          if (currentWordRef.current === found.startIndex) return;

          // Track immediately so subsequent moves over the same word are skipped
          currentWordRef.current = found.startIndex;

          // Brief delay before showing tooltip (prevents flicker between adjacent words)
          if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
          tooltipTimerRef.current = setTimeout(() => {
            setTooltip(found);
          }, 50);
        } else {
          // No word (gap) — 100ms delay before clearing
          if (currentWordRef.current === null) return;

          currentWordRef.current = null;
          if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
          tooltipTimerRef.current = setTimeout(() => {
            setTooltip(null);
          }, 100);
        }
      },
      [hasMatchesFlag, hitTestRestricted],
    );

    const handleMouseLeave = React.useCallback(() => {
      if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
      currentWordRef.current = null;
      setTooltip(null);
    }, []);

    // Build mirror content with data-attributed spans for hit-testing
    const mirrorContent = React.useMemo(() => {
      if (!hasMatchesFlag) return null;

      const parts: React.ReactNode[] = [];
      let cursor = 0;

      for (const m of matches) {
        if (m.start > cursor) {
          parts.push(
            <span key={`t-${cursor}`}>{value.slice(cursor, m.start)}</span>,
          );
        }
        parts.push(
          <span
            key={`r-${m.start}`}
            data-restricted="true"
            data-severity={m.severity}
            data-message={m.displayName}
            data-suggestion={m.suggestion || ""}
            data-text={value.slice(m.start, m.end)}
            data-start={String(m.start)}
          >
            {value.slice(m.start, m.end)}
          </span>,
        );
        cursor = m.end;
      }
      if (cursor < value.length) {
        parts.push(<span key={`t-${cursor}`}>{value.slice(cursor)}</span>);
      }

      return parts;
    }, [value, matches, hasMatchesFlag]);

    // ── Render ───────────────────────────────────────────────────

    return (
      <div className="grid w-full gap-1.5">
        {/* Textarea + overlay */}
        <div ref={containerRef} className="relative overflow-visible">
          {/* Visual overlay behind textarea */}
          {overlay}

          <textarea
            ref={textareaRef}
            id={id}
            value={value}
            onChange={handleChange}
            onBlur={onBlur}
            onScroll={handleScroll}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            placeholder={placeholder}
            disabled={disabled}
            spellCheck
            className={cn(
              "relative flex min-h-[100px] w-full rounded-md border px-3 py-2 text-sm",
              "bg-transparent border-border text-foreground",
              "placeholder:text-muted-foreground",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
              error && "border-red-500",
              className,
            )}
            style={{ overflowY: "auto", scrollbarGutter: "stable" }}
          />

          {/* Hidden mirror div for hit-testing */}
          {hasMatchesFlag && (
            <div
              ref={mirrorRef}
              aria-hidden
              className="pointer-events-none absolute inset-0 whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-sm"
              style={{
                color: "transparent",
                caretColor: "transparent",
                overflowY: "auto",
                scrollbarGutter: "stable",
                willChange: "scroll-position",
              }}
            >
              {mirrorContent}
            </div>
          )}

          {/* Tooltip for restricted words — portalled to body to avoid dialog clipping */}
          {tooltip && (() => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return null;

            const tooltipHalfWidth = 100; // ~half of min-w-[200px]
            const padding = 8;
            const clampedX = Math.max(
              tooltipHalfWidth + padding,
              Math.min(tooltip.x, rect.width - tooltipHalfWidth - padding),
            );
            const flipBelow = tooltip.y < 40;

            return createPortal(
              <div
                className="pointer-events-none fixed z-[100] min-w-[200px] max-w-xs rounded-md border border-border bg-popover px-3 py-2 shadow-md"
                style={{
                  left: rect.left + clampedX,
                  top: rect.top + (flipBelow ? tooltip.y + 24 : tooltip.y - 6),
                  transform: flipBelow ? "translateX(-50%)" : "translate(-50%, -100%)",
                }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span
                    className={cn(
                      "inline-block h-2 w-2 rounded-full shrink-0",
                      tooltip.severity === "block"
                        ? "bg-red-500"
                        : "bg-amber-500",
                    )}
                  />
                  <span
                    className={cn(
                      "text-[10px] font-medium uppercase tracking-wide",
                      tooltip.severity === "block"
                        ? "text-red-600 dark:text-red-400"
                        : "text-amber-600 dark:text-amber-400",
                    )}
                  >
                    {tooltip.severity === "block" ? "Blocked" : "Warning"}
                  </span>
                </div>
                <p className="text-xs font-medium text-foreground">{tooltip.message}</p>
                <p className="text-xs text-muted-foreground">
                  {tooltip.suggestion ? (
                    <>Suggested: <span className="font-medium text-foreground">{tooltip.suggestion}</span></>
                  ) : (
                    "No replacement suggested"
                  )}
                </p>
              </div>,
              document.body,
            );
          })()}
        </div>

        {/* Abbreviation hint */}
        {hasAbbreviations && (
          <>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <span>Type an abbreviation +</span>
              <kbd className="pointer-events-none inline-flex h-5 items-center rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                Space
              </kbd>
              <span>to expand —</span>
              <button
                type="button"
                onClick={() => setAbbreviationsOpen(true)}
                className="inline-flex items-center text-xs font-medium text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
              >
                View abbreviations
              </button>
            </div>
            <NarrativeAbbreviationsDialog
              open={abbreviationsOpen}
              onOpenChange={setAbbreviationsOpen}
              abbreviations={abbreviations!}
            />
          </>
        )}

        {/* Error */}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  },
);

NarrativeTextArea.displayName = "NarrativeTextArea";

export default NarrativeTextArea;
